// src/ble/infrastructures/spjsStandCentral.ts
import { Observable } from 'rxjs'
import {
  openBluetoothAdapter,
  closeBluetoothAdapter,
  startBluetoothDevicesDiscovery,
  stopBluetoothDevicesDiscovery,
  onBluetoothDeviceFound,
  offBluetoothDeviceFound,
  createBLEConnection,
  closeBLEConnection,
  getBLEDeviceServices,
  getBLEDeviceCharacteristics,
} from '@chargespot/spjs'
import { Device, DeviceService } from '@chargespot/spjs/es/bluetooth'
import { StandPeripheral } from '@/ble/interfaces/standPeripheral'
import {
  SpjsStandPeripheral,
  WRITE_WITHOUT_RESPONSE,
} from './spjsStandPeripheral'
import { createScanDedup, resolveScanName } from './scanMatching'
import { BleCharacteristicCapabilityException } from '@/ble/errors/bleCharacteristicCapability'
import { BluetoothNotOnException } from '@/ble/errors/bluetoothNotOn'
import { AdapterSerializer } from './adapterSerializer'
import { withBridgeCap } from './bridgeCall'
import { BRIDGE_TIMEOUTS_MS } from './bleConfig'
import { BridgeCallTimeoutException } from '@/ble/errors/bridgeCallTimeout'
import {
  V14_TX_CHARACTERISTIC,
  V14_RX_CHARACTERISTIC,
  AES_TX_CHARACTERISTIC,
  AES_RX_CHARACTERISTIC,
} from '@/ble/infrastructures/standProfile'

export type ScannedDevice = {
  deviceId: string
  name: string | null // 解決済み（広告名優先・#6095 キャッシュ名対策）
  localName?: string | null // 広告名の生値（デバッグ可視化用）
  cachedName?: string | null // spjs Device.name の生値＝iOSキャッシュ名（デバッグ用）
  rssi?: number // 受信信号強度（近い機体ほど大。名前無し機の識別に使う）
  advertisServiceUUIDs: string[]
}

export type GattDescription = Array<{
  service: string
  characteristics: Array<{
    uuid: string
    write: boolean
    notify: boolean
    read: boolean
    indicate: boolean
  }>
}>

export class SpjsStandCentral {
  // adapter 操作の直列化キュー（発行順=完了順を機構保証）。open 進行中に close が
  // 発行されても、close は open の完了を待ってから native へ届く。逆に close 完了前の
  // 再 open も待つ。spjs の交差時挙動が未文書でも、そもそも交差させないための機構。
  private readonly adapterQueue = new AdapterSerializer()

  async openAdapter(): Promise<void> {
    // Promise.resolve ラップは mock が undefined を返しても安全にするため（scan$ と同じ理由）。
    // cap は op がキューで実行開始してから計時される（per-op 上限・#7706 G2:
    // 1件の無応答でキューが恒久停止しない）。
    return this.adapterQueue.run(() =>
      withBridgeCap(
        Promise.resolve(openBluetoothAdapter()),
        BRIDGE_TIMEOUTS_MS.openAdapter,
        'openBluetoothAdapter'
      )
    )
  }
  async closeAdapter(): Promise<void> {
    return this.closeAdapterAfter()
  }
  // adapter close をキューへ「即時に発行（予約）」しつつ、after（接続 close 等）の settle を
  // 待ってから実 close する（#7706）: detach された後始末の途中でユーザーが即再試行しても、
  // 後続の open はこの予約の後ろに並ぶ（JS 側の発行順の保証）。
  // 【保証の限界】cap で放棄した呼び出しの native 側実処理は取り消せず、その完了順序は
  // SDK に保証記載がない（Inforich 照会中・aikasa/ikasa_platform#7571）。放棄した旧 close が
  // 遅れて完了し新セッションに作用する可能性は残るが、影響は直後の1操作の有界な失敗
  // （scan timeout→接続失敗画面→再試行で回復。各操作は入口で open を発行し直す）に限られる。
  // after は呼び出し側で有界化済み（withBridgeCap）が前提。close 失敗・無応答は発生源で
  // swallow（操作エラーに化けさせない）。warn は観測点として残す。このメソッドは reject しない契約。
  async closeAdapterAfter(after?: Promise<unknown>): Promise<void> {
    return this.adapterQueue.run(async () => {
      if (after !== undefined) {
        // after は呼び出し側で有界化済みが契約だが、契約破りでキューが毒化しないよう
        // 機構でも防御する（cap は closeConnection と同値・超過は待ちを打ち切るだけ）。
        await withBridgeCap(
          Promise.resolve(after).catch(() => undefined),
          BRIDGE_TIMEOUTS_MS.closeConnection,
          'closeAdapterAfter(after)'
        ).catch(() => undefined)
      }
      await withBridgeCap(
        Promise.resolve(closeBluetoothAdapter()),
        BRIDGE_TIMEOUTS_MS.closeAdapter,
        'closeBluetoothAdapter'
      ).catch((e) => console.warn('[ble] adapter close failed (ignored)', e))
    })
  }

  // scan結果(累積emit)を dedup して流す。unsubscribe で discovery停止+callback解除。
  // adapter open は操作の入口=scan購読に内包する（aikasa/ikasa_platform#7681）。spjs は
  // open→discovery の順序が前提（README の標準フロー・旧実装と同順）。open 失敗（BT off等）
  // は発生源で BluetoothNotOnException 化して error 終端し、repository の classifyFailure
  // （instanceof 分類）へそのまま届く。
  scan$(): Observable<ScannedDevice> {
    return new Observable<ScannedDevice>((subscriber) => {
      const dedup = createScanDedup()
      const handler = (res: { devices: Device[] }) => {
        for (const d of res.devices) {
          const name = resolveScanName(d.localName, d.name)
          // dedup は解決済み名で行う（キャッシュ→広告名の確定遷移を別キーで通す）。
          if (!dedup({ deviceId: d.deviceId, name: name ?? undefined }))
            continue
          subscriber.next({
            deviceId: d.deviceId,
            name,
            localName: d.localName ?? null,
            cachedName: d.name ?? null,
            rssi: d.RSSI,
            // spjs実機は advertisServiceUUIDs が配列でないことがある（型は string[]?
            // だが iOS実機で非配列。?? は null/undefined しか弾けないため isArray で防御）。
            advertisServiceUUIDs: Array.isArray(d.advertisServiceUUIDs)
              ? d.advertisServiceUUIDs
              : [],
          })
        }
      }
      // handler 登録は同期のまま（既存挙動維持）。discovery 開始前の found イベントは実機では来ない。
      onBluetoothDeviceFound(handler)
      let cancelled = false
      ;(async () => {
        try {
          // 直列化キュー経由で open する（前操作の close が in-flight なら完了を待つ）。
          await this.openAdapter()
        } catch (e) {
          // ブリッジ無応答(cap超過)は「BT が off」とは別事象なので BluetoothNotOnException に
          // 化けさせない（設定誘導ダイアログの誤表示防止・#7706）。そのまま error 終端し
          // UnlockPage の既存分類で UnknownError 画面になる（native の接続系失敗と同じ扱い）。
          if (e instanceof BridgeCallTimeoutException) throw e
          throw new BluetoothNotOnException(e)
        }
        // open の await 中に unsubscribe された場合、ここで discovery を開始すると
        // teardown の stop が先に走っていて誰も止められない。開始自体を抑止する。
        if (cancelled) return
        // 本番 UnlockPage と同じく引数なしで開始。reject は握りつぶさず subscriber へ流す。
        await startBluetoothDevicesDiscovery()
      })().catch((e) => subscriber.error(e))
      return () => {
        cancelled = true
        offBluetoothDeviceFound(handler)
        // 停止は fire-and-forget（teardown は同期契約）だが、reject を unhandledrejection に
        // しない・停止失敗を観測可能にする（.catch+warn は closeAdapter と同じ規律・#7706 G5）。
        void Promise.resolve(stopBluetoothDevicesDiscovery()).catch((e) =>
          console.warn('[ble] stop discovery failed (ignored)', e)
        )
      }
    })
  }

  async connect(
    device: ScannedDevice,
    options?: {
      // discovery後の識別情報(advertisedName/serviceUuid)から writeType を決める callback。
      // protocol知識（V14=without-response / 旧AES=with-response）は呼び出し側
      // （repository層）に閉じ、この層は既定値のみ持つ非依存のまま。
      writeTypeFor?: (info: {
        advertisedName: string | null
        serviceUuid: string
      }) => number
    }
  ): Promise<StandPeripheral> {
    await withBridgeCap(
      createBLEConnection({ deviceId: device.deviceId }),
      BRIDGE_TIMEOUTS_MS.createConnection,
      'createBLEConnection'
    )
    // strong exception guarantee: connect は「完全に接続済みの StandPeripheral を返す」か
    // 「痕跡を残さず失敗する」かのどちらか。createBLEConnection 成功後に特性探索/notify有効化が
    // 失敗したら、開いた接続と（生成済みなら）登録済み notify リスナーを後始末してから再throwする。
    // platform は repository 層で後始末する方式だが、ここで発生源を塞ぐことで、呼び出し側
    // （デバッグ画面のリトライ / 将来の repository）の実装に依らず接続・リスナーを漏らさない。
    let peripheral: SpjsStandPeripheral | undefined
    try {
      const { services } = await withBridgeCap(
        getBLEDeviceServices({ deviceId: device.deviceId }),
        BRIDGE_TIMEOUTS_MS.discoverServices,
        'getBLEDeviceServices'
      )
      const found = await this.findWriteNotifyChars(device.deviceId, services)
      if (!found) {
        throw new BleCharacteristicCapabilityException(
          services.map((s) => s.uuid).join(',')
        )
      }
      // advertisedName は peripheral に渡す値（device.name、scan$解決済み）と同じものを
      // writeTypeFor へ渡す。callback が見る名前と peripheral が持つ名前を一致させる。
      const advertisedName = device.name
      const writeType =
        options?.writeTypeFor?.({
          advertisedName,
          serviceUuid: found.serviceUuid,
        }) ?? WRITE_WITHOUT_RESPONSE
      peripheral = new SpjsStandPeripheral({
        deviceId: device.deviceId,
        advertisedName,
        serviceUuid: found.serviceUuid,
        writeCharacteristicId: found.writeUuid,
        notifyCharacteristicId: found.notifyUuid,
        // 既定は V14(fff1) 前提の write-without-response。旧AES 等プロトコル依存の
        // writeType は options.writeTypeFor（呼び出し側=repository層）が決定する。
        writeType,
        // 切断の畳みの adapter close も central の直列化キューを通す（発行点の一元化）。
        // 予約(発行)は同期・実closeはafter(接続closeのsettle)待ち（#7706）。
        closeAdapter: (after) => this.closeAdapterAfter(after),
      })
      await peripheral.startNotifications()
      return peripheral
    } catch (e) {
      // 後始末自体の失敗で元の例外を隠さないよう、cleanup の reject は握りつぶす。
      if (peripheral) {
        // peripheral 生成済み: off リスナー + closeBLEConnection をまとめて実行。
        await peripheral.disconnect().catch(() => undefined)
      } else {
        // peripheral 未生成: 開いた接続のみ閉じる。後始末の close も無応答になり得るため
        // cap を通す（reject/cap 超過とも swallow・元の例外を隠さない）。
        await withBridgeCap(
          closeBLEConnection({ deviceId: device.deviceId }),
          BRIDGE_TIMEOUTS_MS.closeConnection,
          'closeBLEConnection'
        ).catch(() => undefined)
      }
      throw e
    }
  }

  // デバッグ用: 接続中デバイスの全サービス/特性と properties を列挙（GATT全体把握）。
  // 正しいコマンドサービスの特定や、findWriteNotifyChars の選択が妥当かの検証に使う。
  async describeServices(deviceId: string): Promise<GattDescription> {
    const { services } = await withBridgeCap(
      getBLEDeviceServices({ deviceId }),
      BRIDGE_TIMEOUTS_MS.discoverServices,
      'getBLEDeviceServices'
    )
    const result: GattDescription = []
    for (const svc of services) {
      const { characteristics } = await withBridgeCap(
        getBLEDeviceCharacteristics({ deviceId, serviceId: svc.uuid }),
        BRIDGE_TIMEOUTS_MS.discoverCharacteristics,
        'getBLEDeviceCharacteristics'
      )
      result.push({
        service: svc.uuid,
        characteristics: characteristics.map((c) => ({
          uuid: c.uuid,
          write: c.properties.write,
          notify: c.properties.notify,
          read: c.properties.read,
          indicate: c.properties.indicate,
        })),
      })
    }
    return result
  }

  // platform _ensureV14Characteristics / write() 同方針: 既知の TX/RX 特性を UUID で直接マッチ。
  // V14: fff1(TX/write)/fff2(RX/notify)、旧AES: 6e400002/6e400003。
  // 「先頭の write/notify 特性」を拾うと FFF6 等の汎用サービスを誤選択する（実機 ik_C0134 で
  // FFF6 誤選択→no notify を観測）。既知UUID一致を最優先し、見つからないハードのみ
  // 従来の動的(property)選択へフォールバックする。
  private isKnownTxChar(uuid: string): boolean {
    const u = uuid.toLowerCase()
    return (
      u === V14_TX_CHARACTERISTIC || u === 'fff1' || u === AES_TX_CHARACTERISTIC
    )
  }

  private isKnownRxChar(uuid: string): boolean {
    const u = uuid.toLowerCase()
    return (
      u === V14_RX_CHARACTERISTIC || u === 'fff2' || u === AES_RX_CHARACTERISTIC
    )
  }

  private async findWriteNotifyChars(
    deviceId: string,
    services: DeviceService[]
  ) {
    let knownWrite: { serviceUuid: string; uuid: string } | null = null
    let knownNotify: { serviceUuid: string; uuid: string } | null = null
    let dynamic: {
      serviceUuid: string
      writeUuid: string
      notifyUuid: string
    } | null = null

    for (const svc of services) {
      const svcUuid = svc.uuid.toLowerCase()
      if (svcUuid.startsWith('00001800') || svcUuid.startsWith('00001801')) {
        continue // generic access/attribute は除外（UUIDは大小文字を正規化して判定）
      }
      const { characteristics } = await withBridgeCap(
        getBLEDeviceCharacteristics({ deviceId, serviceId: svc.uuid }),
        BRIDGE_TIMEOUTS_MS.discoverCharacteristics,
        'getBLEDeviceCharacteristics'
      )
      for (const c of characteristics) {
        if (!knownWrite && this.isKnownTxChar(c.uuid)) {
          knownWrite = { serviceUuid: svc.uuid, uuid: c.uuid }
        }
        if (!knownNotify && this.isKnownRxChar(c.uuid)) {
          knownNotify = { serviceUuid: svc.uuid, uuid: c.uuid }
        }
      }
      // 既知UUIDが無いハード向けフォールバック: 最初の write+notify 両持ちサービス。
      if (!dynamic) {
        const writeChar = characteristics.find((c) => c.properties.write)
        const notifyChar = characteristics.find((c) => c.properties.notify)
        if (writeChar && notifyChar) {
          dynamic = {
            serviceUuid: svc.uuid,
            writeUuid: writeChar.uuid,
            notifyUuid: notifyChar.uuid,
          }
        }
      }
    }

    if (knownWrite && knownNotify) {
      // 現行プロファイル(V14: fff0配下 / 旧AES: 6e400001配下)は TX/RX が同一 service に同居する前提。
      // 別 service にまたがると notify を write 側の serviceId で誤登録してしまうため明示的に弾く。
      if (knownWrite.serviceUuid !== knownNotify.serviceUuid) {
        throw new BleCharacteristicCapabilityException(
          `TX(${knownWrite.serviceUuid})/RX(${knownNotify.serviceUuid}) が別サービス`
        )
      }
      return {
        serviceUuid: knownWrite.serviceUuid,
        writeUuid: knownWrite.uuid,
        notifyUuid: knownNotify.uuid,
      }
    }
    return dynamic
  }
}

// UnlockPage の mount ごとに central を生成すると adapter 直列化キューがインスタンス毎に分裂し、
// キャンセル直後の素早い再入場で「旧キューの close」と「新キューの open」が native ブリッジ上で
// 交差し得る（#7706 G3。AdapterSerializer の保証はインスタンス内にしか効かない）。
// native の StandCentral が static singleton であるのに合わせ、本番はモジュール単位で共有する。
// テストは従来どおり DI（useStandOperation の deps.central）で差し替える。
let sharedInstance: SpjsStandCentral | null = null
export function getSharedSpjsStandCentral(): SpjsStandCentral {
  if (sharedInstance === null) sharedInstance = new SpjsStandCentral()
  return sharedInstance
}
